import ActionButtonLink from "../components/action-button-link"
import FeedbackContainer from "../components/feedback-container"
import FeedbackHeadline from "../components/feedback-headline"
import ProgressCircle from "../components/progress-circle"
import ResponsiveContainer from "../components/responsive-container"
import ResponsiveContent from "../components/responsive-content"
import { useMessages } from "../language/language"
import "./final-screen-frame.less"
import { useLogout } from "play/hooks/logout-hook"
import { useMemo } from "react"
import AdminUrls from "admin/routes/admin-urls"
import { usePlayState } from "play/hooks/use-play-state"
import { LeaderboardStudent } from "play/play-model"
import ActionButton from "play/components/action-button"
import playEnvironment from "play/play-environment"

const PODIUM_CUP = "/img/final_screen/cup.png"
const PODIUM_GROUND = "/img/final_screen/ground.png"

type PodiumPlaceKind = "first" | "second" | "third"

export default function FinalScreenFrame() {
  const msg = useMessages()
  const {
    accuracy,
    isTeacherUrl,
    reportId,
    newGameLink,
    newGameMessage,
    leaderboard,
  } = usePlayState()
  const correct = accuracy >= 50
  const logout = useLogout()

  const firstThree = useMemo(() => {
    const sorted = leaderboard.slice().sort((x, y) => y.points - x.points)
    return {
      first: sorted.length > 0 ? sorted[0] : null,
      second: sorted.length > 1 ? sorted[1] : null,
      third: sorted.length > 2 ? sorted[2] : null,
    }
  }, [leaderboard])

  const hasPodium = firstThree.first !== null

  let title: string
  if (accuracy >= 90) {
    title = msg.finalScreenPage_tier1
  } else if (accuracy >= 70) {
    title = msg.finalScreenPage_tier2
  } else if (accuracy >= 50) {
    title = msg.finalScreenPage_tier3
  } else {
    title = msg.finalScreenPage_tier4
  }

  return (
    <div className="final-screen-frame">
      <div className="final-screen-content">
        <FeedbackContainer>
          <FeedbackHeadline>{title}</FeedbackHeadline>
        </FeedbackContainer>
        <ProgressCircle percent={accuracy} correct={correct} />

        {hasPodium && (
          <div className="podium">
            <div className="podium-row">
              <PodiumPlace kind="second" student={firstThree.second} />
              <PodiumPlace
                kind="first"
                student={firstThree.first}
                showCup={true}
              />
              <PodiumPlace kind="third" student={firstThree.third} />
            </div>
            <img
              className="podium-ground"
              src={PODIUM_GROUND}
              alt=""
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      <ResponsiveContainer
        className="final-screen-footer"
        centerContents={true}
      >
        <ResponsiveContent className="final-screen-footer-content">
          {newGameLink && newGameMessage ? (
            <ActionButtonLink className="new" href={newGameLink}>
              {newGameMessage}
            </ActionButtonLink>
          ) : (
            <ActionButton
              className="new"
              onClick={() => {
                playEnvironment.connectionId = null
                location.assign(logout.logoutLink)
              }}
            >
              {msg.finalScreenPage_new}
            </ActionButton>
          )}

          {isTeacherUrl && reportId !== null && (
            <ActionButtonLink
              className="results"
              href={`/${msg.locale_code}/admin${AdminUrls.viewReportByScreen(reportId)}`}
              target="_blank"
            >
              {msg.finalScreenPage_showResults}
            </ActionButtonLink>
          )}
        </ResponsiveContent>
      </ResponsiveContainer>
    </div>
  )
}

function PodiumPlace(props: {
  kind: PodiumPlaceKind
  student: LeaderboardStudent | null
  showCup?: boolean
}) {
  const msg = useMessages()
  const { kind, student, showCup } = props

  const pointsLabel = useMemo(() => {
    if (!student) return null
    return msg.pointsToString(student.points)
  }, [student?.points, msg.pointsToString])

  // Empty slot — keep the column to preserve the row layout, but render
  // nothing inside it. Animation still applies so the row reveals together.
  const isEmpty = student === null

  return (
    <div
      className={`podium-place podium-place-${kind}${isEmpty ? " is-empty" : ""}`}
    >
      {showCup && <img className="podium-cup" src={PODIUM_CUP} alt="" />}
      {!isEmpty && (
        <div className="podium-label">
          <div className="podium-name">{student!.fullName}</div>
          <div className="podium-points">{pointsLabel}</div>
        </div>
      )}
      <div className="podium-block" aria-hidden="true" />
    </div>
  )
}
